import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { STATUS_META, memberTypeLabel } from "@/lib/constants";
import { ApplicationTracker } from "@/components/ApplicationTracker";
import { OtherDevices } from "@/components/OtherDevices";
import { GRADES, GRADE_RENAMES, FACILITY_TYPES, RANKS, INTERN_FIELDS, formatGhanaCard, profileErrors, mapFacilityType, legacyChildren } from "@/lib/profileForm";
import { ChildrenEditor } from "@/components/ChildrenEditor";
import { PreviousFacilityPicker, OTHER_FACILITY } from "@/components/PreviousFacilityPicker";
import { LiveCertificate } from "@/components/MemberCertificate";
import { MySouvenirs } from "@/components/souvenirs/MySouvenirs";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  IdentificationBadge, PaperPlaneTilt, FloppyDisk, SealCheck,
  Camera, Plus, Trash, GraduationCap, User, Phone, Users, Briefcase, MagnifyingGlass, NotePencil, TShirt, Coins, ListBullets,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { CustomField, useFormLayout, isVisible, visibleAnswers } from "@/components/CustomField";

const SECTION_ICONS = { biodata: User, contact: Phone, next_of_kin: Users, employment: Briefcase, identification: IdentificationBadge,
  emergency: Phone, family: Users, parents: Users, previous: Briefcase, dues: Coins, souvenirs: TShirt, education: GraduationCap };
const WIDE_BLOCKS = ["photo", "grade_cadre", "facility", "marital_status", "children", "previous_employment", "dues", "education"];

const TSHIRT_SIZES = ["Small", "Medium", "Large", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL"];
const TITLES = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Rev.", "Madam", "MLS"];
const MARITAL_STATUSES = ["Single", "Married", "Divorced", "Widowed"];
const PARENT_STATUSES = ["Alive", "Deceased"];
const EMPTY_EDU = { institution: "", location: "", entry_year: "", graduation_year: "", qualification: "" };

function Section({ icon: Icon, title, children }) {
  return (
    <Card className="p-6 border-border">
      <h2 className="font-display text-lg font-bold flex items-center gap-2">
        <Icon size={20} weight="bold" className="text-primary" /> {title}
      </h2>
      <div className="mt-5">{children}</div>
    </Card>
  );
}

export default function Profile({ adminMemberId }) {
  const admin = !!adminMemberId;
  const navigate = useNavigate();
  const [pendingEdit, setPendingEdit] = useState(false);
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const intern = profile?.member_type === "intern";
  const portal = ["intern", "private"].includes(profile?.member_type);
  const [form, setForm] = useState({});
  const [education, setEducation] = useState([]);
  const [photo, setPhoto] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [orgs, setOrgs] = useState({ regions: [], chapters: [], facilities: [] });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadError, setLoadError] = useState("");
  const errorRef = useRef(null);
  const [gcCheck, setGcCheck] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [prevMode, setPrevMode] = useState("");
  const layout = useFormLayout(profile?.member_type);

  useEffect(() => {
    const v = (form.ghana_card_number || "").trim();
    if (!v) { setGcCheck(null); return; }
    const fmt = /^GHA-\d{9}-\d$/i.test(v);
    if (!fmt) { setGcCheck({ valid_format: false, available: false }); return; }
    setGcCheck({ checking: true });
    let active = true;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/ghana-card/check", { params: { value: v, ...(admin ? { member_id: adminMemberId } : {}) }, signal: controller.signal });
        if (active) setGcCheck(data);
      } catch { if (active) setGcCheck({ unavailable: true }); }
    }, 500);
    return () => { active = false; clearTimeout(t); controller.abort(); };
  }, [form.ghana_card_number]);

  // facility lookup
  const [facQuery, setFacQuery] = useState("");
  const [suggest, setSuggest] = useState([]);
  const [fallback, setFallback] = useState(false);
  const [refRegions, setRefRegions] = useState([]);
  const [refDistricts, setRefDistricts] = useState([]);
  const [refFacilities, setRefFacilities] = useState([]);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const loadProfile = useCallback(async () => {
    try {
      const [published, request, history] = admin
        ? await api.get(`/members/${adminMemberId}/admin-profile`).then((r) => {
          setPendingEdit(r.data.pending_edit);
          return [{ data: r.data.member }, { data: null }, { data: [] }];
        })
        : await Promise.all([api.get("/me/profile"), api.get("/me/edit-request"), api.get("/me/edit-requests")]);
      const staged = ["pending_review", "reviewing"].includes(request.data?.status)
        ? Object.fromEntries(Object.entries(request.data.changes || {}).map(([k, ch]) => [k, ch.new])) : {};
      const d = { ...published.data, ...staged };
      d.professional_grade = GRADE_RENAMES[d.professional_grade] || d.professional_grade;
      d.worked_at_previous_facility = (d.worked_at_previous_facility || "").toLowerCase();
      Object.assign(d, legacyChildren(d));
      d.facility_type = d.facility_type ? mapFacilityType(d.facility_type) : "";
      d.previous_facility_type = d.previous_facility_type ? mapFacilityType(d.previous_facility_type) : "";
      setPrevMode("");
      setProfile(published.data); setEditReq(request.data); setEditHistory(history.data);
      setForm(d); setEducation(d.education || []); setPhoto(d.profile_picture || "");
      setFacQuery(""); setLoadError("");
    } catch { setLoadError(admin ? "This member could not be loaded." : "Your profile could not be loaded. Please try again."); }
  }, [admin, adminMemberId]);
  useEffect(() => {
    window.addEventListener("member-type-updated", loadProfile);
    return () => window.removeEventListener("member-type-updated", loadProfile);
  }, [loadProfile]);

  useEffect(() => {
    api.get("/orgs").then((r) => setOrgs(r.data));
    api.get("/facilities-reference/regions").then((r) => setRefRegions(r.data));
    loadProfile();
  }, [loadProfile]);

  const doSearch = useCallback(async (q) => {
    setFacQuery(q); set("facility_name", q);
    if (q.length < 2) { setSuggest([]); return; }
    const { data } = await api.get("/facilities-reference/search", { params: { q } });
    setSuggest(data);
  }, []); // eslint-disable-line

  const pickFacility = (f) => {
    setForm((p) => ({ ...p, facility_name: f.name, facility_type: mapFacilityType(f.type), town: f.town, district: f.district, region_name: f.region }));
    setFacQuery(f.name); setSuggest([]);
  };

  const pickRegion = async (region) => {
    set("region_name", region); set("district", ""); set("facility_name", ""); set("facility_type", ""); set("town", "");
    setRefDistricts([]); setRefFacilities([]);
    const { data } = await api.get("/facilities-reference/districts", { params: { region } });
    setRefDistricts(data);
  };
  const pickDistrict = async (district) => {
    set("district", district); set("facility_name", ""); set("facility_type", "");
    const { data } = await api.get("/facilities-reference/facilities", { params: { region: form.region_name, district } });
    setRefFacilities(data);
  };
  const pickRefFacility = (name) => {
    const f = refFacilities.find((x) => x.name === name);
    if (f) setForm((p) => ({ ...p, facility_name: f.name, facility_type: mapFacilityType(f.type), town: f.town }));
  };

  useEffect(() => {
    if (!portal || !form.region_name) return;
    let active = true;
    api.get("/facilities-reference/districts", { params: { region: form.region_name } }).then(({ data }) => { if (active) setRefDistricts(data); });
    if (form.district) api.get("/facilities-reference/facilities", { params: { region: form.region_name, district: form.district } }).then(({ data }) => { if (active) setRefFacilities(data); });
    return () => { active = false; };
  }, [portal, form.region_name, form.district]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setPhotoUploading(true);
    try {
      const { data } = await api.post("/upload/profile-picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPhotoUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const photoSrc = (p) => {
    if (!p) return "";
    if (p.startsWith("data:") || p.startsWith("http")) return p;
    return `${API}/files/${p}?auth=${localStorage.getItem("token")}`;
  };

  const addEdu = () => { if (education.length < 5) setEducation([...education, { ...EMPTY_EDU }]); };
  const setEdu = (i, k, v) => setEducation(education.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const rmEdu = (i) => setEducation(education.filter((_, idx) => idx !== i));

  const payload = () => ({ data: { ...pickFields(form), profile_picture: photo, education, custom_fields: form.custom_fields || {}, ...(intern ? {} : { children: form.children || [] }) } });
  const pickFields = (f) => {
    const keys = ["title", "first_name", "middle_name", "last_name", "date_of_birth", "sex", "contact_number",
      "alternate_contact_number", "email", "next_of_kin_name", "next_of_kin_contact",
      "next_of_kin_relationship", "professional_grade", "rank", "facility_name", "facility_type",
      "town", "district", "region_name", "tshirt_size", "pays_dues", "dues_mode",
      "emergency_contact_name", "emergency_contact_email", "emergency_contact_phone", "emergency_contact_address",
      "marital_status", "spouse_name", "no_children",
      "mother_name", "mother_status", "father_name", "father_status", "ghana_card_number",
      "worked_at_previous_facility", "previous_facility_name", "previous_facility_location", "previous_facility_type"];
    return (intern ? INTERN_FIELDS : keys).reduce((a, k) => ({ ...a, [k]: f[k] ?? "" }), {});
  };

  const validate = () => {
    const next = profileErrors(form, photo);
    if (gcCheck?.valid_format && gcCheck.available === false) next.ghana_card_number = "This Ghana Card is already registered";
    if (photoUploading) next.profile_picture = "Please wait for your photo to finish uploading";
    setErrors(next);
    if (Object.keys(next).length) {
      requestAnimationFrame(() => { errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); errorRef.current?.focus(); });
      return false;
    }
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", payload());
      setProfile(data); toast.success("Profile saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.put("/me/profile", payload());
      const { data } = await api.post("/me/submit-application");
      setProfile(data); await refreshUser();
      toast.success("Application submitted for approval");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const submitEdits = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (admin) {
        const { data } = await api.put(`/members/${adminMemberId}/profile`, payload());
        toast.success(data.changed.length ? `Member updated: ${data.changed.join(", ")}` : "No changes to save");
      } else {
        const { data } = await api.put("/me/profile", payload());
        toast.success(data.status === "withdrawn" ? "Pending changes withdrawn. Published details are unchanged." : "Changes saved for National Manager approval. Published details are unchanged.");
      }
      setEditMode(false);
      await loadProfile();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (loadError) return <Layout><p role="alert" data-testid="profile-load-error">{loadError}</p><Button onClick={loadProfile} data-testid="profile-load-retry">Try again</Button></Layout>;
  if (!profile) return <Layout><p className="text-muted-foreground" data-testid="profile-loading">Loading…</p></Layout>;
  const st = STATUS_META[profile.is_deceased ? "deceased" : profile.application_status] || STATUS_META.incomplete;
  const approved = profile.application_status === "approved";
  const applicationFlow = !admin && profile.role === "member" && ["incomplete", "rejected", null, undefined].includes(profile.application_status) && !profile.membership_id;
  const editing = !applicationFlow;
  const editable = applicationFlow || editMode;
  const cancelEdit = async () => { setEditMode(false); setErrors({}); await loadProfile(); };
  const rankOptions = RANKS[form.professional_grade] || [];
  const COMPLETION_FIELDS = intern ? ["first_name", "last_name", "date_of_birth", "sex", "contact_number", "email", "ghana_card_number", "facility_name", "district", "region_name"] : ["first_name", "last_name", "date_of_birth", "sex", "contact_number",
    "email", "facility_name", "professional_grade", "rank", "next_of_kin_name"];
  const FIELD_LABELS = {
    title: "Title", first_name: "First name", middle_name: "Middle name", last_name: "Last name",
    date_of_birth: "Date of birth", sex: "Sex", contact_number: "Contact number",
    alternate_contact_number: "Alternate contact", email: "Email", next_of_kin_name: "Next of kin",
    next_of_kin_contact: "Next of kin contact", next_of_kin_relationship: "Kin relationship",
    professional_grade: "Grade", rank: "Rank", facility_name: "Facility", facility_type: "Facility type",
    town: "Town", district: "District", region_name: "Region", education: "Education",
  };
  const completion = Math.round(
    COMPLETION_FIELDS.filter((f) => String(form[f] || "").trim()).length * 100 / COMPLETION_FIELDS.length);

  const coreLabels = Object.fromEntries((layout?.sections || []).flatMap((s) => s.fields.filter((f) => f.core).map((f) => [f.key, f.label])));
  const L = (k, fallbackLabel) => coreLabels[k] || fallbackLabel;
  const setCustom = (k, v) => setForm((p) => ({ ...p, custom_fields: { ...(p.custom_fields || {}), [k]: v } }));
  const renderSections = (blocks) => { const eff = visibleAnswers(layout, form); return (layout?.sections || [{ id: "all", title: "Membership details", fields: Object.keys(blocks).map((key) => ({ key, core: true })) }]).map((sec) => {
    const items = sec.fields.map((f) => {
      if (!f.core) return isVisible(f, { ...form, custom_fields: eff }) ? <CustomField key={f.key} field={f} value={(form.custom_fields || {})[f.key]} onChange={(v) => setCustom(f.key, v)} disabled={!editable} /> : null;
      if (!blocks[f.key]) return null;
      return <div key={f.key} className={WIDE_BLOCKS.includes(f.key) ? "sm:col-span-2" : ""}>{blocks[f.key]}</div>;
    }).filter(Boolean);
    if (!items.length) return null;
    const Icon = SECTION_ICONS[sec.id] || ListBullets;
    return <Section key={sec.id} icon={Icon} title={sec.title}><div className="grid sm:grid-cols-2 gap-4" data-testid={`profile-section-${sec.id}`}>{items}</div></Section>;
  }); };

  const F = ({ k, label, type = "text", req }) => (
    <div className="space-y-1.5">
      <Label htmlFor={`profile-${k}`}>{L(k, label)}{req && <span className="text-destructive"> *</span>}</Label>
      <Input id={`profile-${k}`} data-testid={`profile-${k}`} type={type} value={form[k] ?? ""} disabled={!editable}
        aria-required={req} aria-invalid={!!errors[k]} onChange={(e) => set(k, e.target.value)} />
    </div>
  );

  return (
    <Layout>
      <div className="animate-fade-up max-w-4xl">
        {admin && <div className="mb-2 -ml-2 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="admin-edit-back">← Back</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`/members/${adminMemberId}/print`, "_blank")} data-testid="admin-print-member">Print</Button>
        </div>}
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{admin ? "National administration" : "Your account"}</span>
        <h1 data-testid="member-profile-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{admin ? `Edit Member: ${profile.first_name || ""} ${profile.last_name || ""}` : intern ? "Intern Application" : portal ? "Private Member Application" : "Member Profile"}</h1>
        <p className="mt-2 text-sm" data-testid="profile-member-type">
          <span className="text-muted-foreground">Type of membership: </span>
          <span className="font-semibold">{memberTypeLabel(profile.member_type)}</span>
          {profile.member_type_requested && <span className="ml-2 text-xs text-amber-700 dark:text-amber-400" data-testid="profile-member-type-pending">Change to {memberTypeLabel(profile.member_type_requested)} awaiting National approval</span>}
        </p>
        {profile.self_registered && !admin && <p data-testid="public-application-path" className="mt-2 text-sm text-muted-foreground">Facility Representative review → Regional Secretary review → National Manager final approval. Your membership number will begin <span className="font-mono font-medium">G</span>. Sign-in email: {profile.login_email}.</p>}
        {portal && <div data-testid="portal-application-path" className="mt-3 space-y-1 text-sm text-muted-foreground">
          <p>{intern ? "Facility Representative review → Regional Secretary final approval" : "Regional Secretary review → National Manager final approval"}. Your membership number will begin <span className="font-mono font-medium">{intern ? "INT-" : "P-"}</span>.</p>
          <p data-testid="portal-login-email">Sign-in email: {profile.login_email}. Updating your contact email below does not change your sign-in address.</p>
        </div>}

        {/* Status */}
        <Card className={`mt-6 p-6 border-border ${approved ? "bg-emerald-50 dark:bg-emerald-500/10" : ""}`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className={`flex h-12 w-12 items-center justify-center rounded-md ${approved ? "bg-emerald-600 text-white" : "bg-primary text-primary-foreground"}`}>
                {approved ? <SealCheck size={26} weight="fill" /> : <IdentificationBadge size={26} weight="bold" />}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Membership ID</p>
                <p data-testid="my-membership-id" className="font-mono text-2xl font-semibold tracking-wider">{profile.membership_id || "Pending approval"}</p>
              </div>
            </div>
            <span data-testid="my-status" className={`text-xs font-medium px-3 py-1.5 rounded-full ${st.cls}`}>{st.label}</span>
          </div>
          {profile.application_status === "rejected" && profile.rejection_reason && (
            <p className="mt-4 text-sm text-destructive">Rejected: {profile.rejection_reason}. Please update and resubmit.</p>
          )}
        </Card>

        {!admin && <ApplicationTracker profile={profile} />}

        {!admin && <OtherDevices />}

        {/* Profile completeness */}
        <Card className="mt-4 p-5 border-border" data-testid="profile-completion-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">Profile completeness</p>
            <span data-testid="profile-completion-pct" className="font-display text-lg font-bold tabular-nums">{completion}%</span>
          </div>
          <Progress value={completion} className="h-2" />
          {completion < 100 && editable && (
            <p className="text-xs text-muted-foreground mt-2">{intern ? "Complete your personal, contact, Ghana Card and placement details before submitting." : "Complete all key fields (name, DOB, sex, contact, email, facility, grade, rank, next of kin) before submitting."}</p>
          )}
        </Card>

        {!admin && <><LiveCertificate />
        <MySouvenirs /></>}
        {/* Self-service profile staging */}
        {editing && (
          <Card className="mt-4 p-5 border-border" data-testid="edit-request-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <NotePencil size={18} weight="bold" className="text-primary" />
                <p className="text-sm font-medium">{admin ? (editMode ? "You are editing this member" : "Edit this member's information") : editMode ? "You are editing your profile" : "Edit your profile anytime"}</p>
              </div>
              {editMode ? (
                <Button variant="outline" size="sm" onClick={cancelEdit} disabled={submitting} data-testid="cancel-edit-profile-button">Cancel editing</Button>
              ) : (
                <Button size="sm" className="gap-2" onClick={() => setEditMode(true)} disabled={admin && pendingEdit} data-testid="edit-profile-button"><NotePencil size={16} weight="bold" /> {admin ? "Edit member" : "Edit profile"}</Button>
              )}
            </div>
            {admin ? (
              <p className={`text-sm ${pendingEdit ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`} data-testid="admin-edit-explanation">
                {pendingEdit ? "This member has a profile change waiting in Profile Approvals. Approve or reject it before editing." : "Changes take effect immediately, are recorded in the Audit Log, and the member is notified of the fields changed. Membership number, staff ID and photo stay locked."}
              </p>
            ) : <>
            <p className="text-sm text-muted-foreground" data-testid="profile-staging-explanation">No prior permission is needed. Save your changes for National Manager approval. Your published details stay unchanged until approval.</p>
            {["pending_review", "reviewing"].includes(editReq?.status) && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-2" data-testid="profile-staging-status">Awaiting National Manager review · Revision {editReq.revision}. The form below shows your saved proposed changes. You can keep editing and save a new revision.</p>
            )}
            {editReq?.status === "approved" && <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-2" data-testid="profile-staging-status">Your latest changes were approved and published.</p>}
            {editReq?.status === "rejected" && <p className="text-sm text-destructive mt-2" data-testid="profile-staging-status">Changes rejected: {editReq.review_note || "Please update and resubmit."} Your published details are unchanged.</p>}

            {editHistory.filter((h) => ["approved", "rejected", "access_rejected"].includes(h.status)).length > 0 && (
              <div className="mt-4 border-t border-border pt-3" data-testid="edit-history">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Past requests</p>
                <div className="space-y-2">
                  {editHistory.filter((h) => ["approved", "rejected", "access_rejected"].includes(h.status)).slice(0, 6).map((h) => {
                    const meta = {
                      approved: { label: "Approved", cls: "text-emerald-600 dark:text-emerald-400" },
                      rejected: { label: "Changes rejected", cls: "text-destructive" },
                      access_rejected: { label: "Access denied", cls: "text-destructive" },
                    }[h.status];
                    const when = h.reviewed_at || h.requested_at;
                    return (
                      <div key={h.id} className="text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className={`font-medium ${meta.cls}`}>{meta.label}</span>
                        <span className="text-muted-foreground">{when ? new Date(when).toLocaleDateString() : ""}</span>
                        {h.status === "approved" && h.changes && (
                          <span className="text-muted-foreground">· updated {Object.keys(h.changes).map((k) => FIELD_LABELS[k] || k).join(", ")}</span>
                        )}
                        {h.review_note && <span className="text-muted-foreground">· “{h.review_note}”</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </>}
          </Card>
        )}

        {Object.keys(errors).length > 0 && (
          <div ref={errorRef} tabIndex={-1} role="alert" data-testid="profile-validation-errors" className="mt-6 rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive">
            <p className="font-semibold">Please correct these fields before submitting:</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">{Object.entries(errors).map(([field, error]) => <li key={field} data-testid={`profile-error-${field}`}>{error}</li>)}</ul>
          </div>
        )}
        <div className={`mt-6 space-y-6 form-contrast ${editable ? "" : "profile-display"}`} data-testid={editable ? "profile-form-edit" : "profile-form-display"}>
          {renderSections({
              photo: (<div className="flex flex-col items-center gap-3">
                <div className="h-28 w-28 rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center">
                  {photo ? <img src={photoSrc(photo)} alt="profile" className="h-full w-full object-cover" /> : <User size={40} className="text-muted-foreground" />}
                </div>
                {editable && !admin && (
                  <>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-photo-input" onChange={onPhoto} />
                    <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={photoUploading} data-testid="upload-photo-button">
                      <Camera size={16} weight="bold" /> {photoUploading ? "Uploading…" : "Upload photo"}
                    </Button>
                    {applicationFlow && <span className="text-xs text-muted-foreground">Required <span className="text-destructive">*</span></span>}
                  </>
                )}
              </div>),
                title: (<div className="space-y-1.5">
                  <Label>{L("title", "Title")}</Label>
                  <Select value={form.title || ""} onValueChange={(v) => set("title", v)} disabled={!editable}>
                    <SelectTrigger data-testid="profile-title"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>),
                first_name: F({ k: "first_name", label: "First name", req: true }),
                middle_name: F({ k: "middle_name", label: "Middle name" }),
                last_name: F({ k: "last_name", label: "Last name", req: true }),
                date_of_birth: F({ k: "date_of_birth", label: "Date of birth", type: "date", req: true }),
                age: (<div className="space-y-1.5">
                  <Label>{L("age", "Age")}</Label>
                  <Input data-testid="profile-age" value={profile.age ?? ""} disabled />
                </div>),
                sex: (<div className="space-y-1.5">
                  <Label>{L("sex", "Sex")} <span className="text-destructive">*</span></Label>
                  <Select value={form.sex || ""} onValueChange={(v) => set("sex", v)} disabled={!editable}>
                    <SelectTrigger data-testid="profile-sex"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent>
                  </Select>
                </div>),
              contact_number: F({ k: "contact_number", label: "Contact number", req: true }),
              alternate_contact_number: F({ k: "alternate_contact_number", label: "Alternate contact number" }),
              email: F({ k: "email", label: "Email address", type: "email", req: true }),
              next_of_kin_name: !intern && F({ k: "next_of_kin_name", label: "Full name" }),
              next_of_kin_contact: !intern && F({ k: "next_of_kin_contact", label: "Contact number" }),
              next_of_kin_relationship: !intern && F({ k: "next_of_kin_relationship", label: "Relationship" }),
              staff_id: !portal && (<div className="space-y-1.5">
                <Label>{L("staff_id", "Staff ID")}</Label>
                <Input data-testid="profile-staff-id" value={profile.staff_id} disabled className="font-mono" />
              </div>),
              ssnit_number: !portal && (<div className="space-y-1.5">
                <Label>{L("ssnit_number", "SSNIT Number")}</Label>
                <Input data-testid="profile-ssnit" value={profile.ssnit_number} disabled className="font-mono" />
              </div>),
              grade_cadre: !intern && (<div className="grid sm:grid-cols-2 gap-4"><div className="space-y-1.5">
                <Label>{L("grade_cadre", "Professional Grade & Cadre").split("&")[0].trim() || "Professional Grade"} <span className="text-destructive">*</span></Label>
                <Select value={form.professional_grade || ""} onValueChange={(v) => { set("professional_grade", v); set("rank", ""); }} disabled={!editable}>
                  <SelectTrigger data-testid="profile-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
                  <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cadre <span className="text-destructive">*</span></Label>
                <Select value={form.rank || ""} onValueChange={(v) => set("rank", v)} disabled={!editable || !form.professional_grade}>
                  <SelectTrigger data-testid="profile-rank" aria-required="true"><SelectValue placeholder={form.professional_grade ? "Select cadre" : "Choose grade first"} /></SelectTrigger>
                  <SelectContent>{rankOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div></div>),

              facility: (<div className="grid gap-4"><div className="space-y-1.5 relative">
                <Label>{L("facility", "Name of Facility")} <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="profile-facility-search" className="pl-9" placeholder="Type to search your facility…"
                    value={facQuery || form.facility_name || ""} disabled={!editable}
                    onChange={(e) => doSearch(e.target.value)} />
                </div>
                {suggest.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="facility-suggestions">
                    {suggest.map((f) => (
                      <button type="button" key={f.name} onClick={() => pickFacility(f)}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-accent">
                        <span className="font-medium">{f.name}</span>
                        <span className="block text-xs text-muted-foreground">{f.type} · {f.district}, {f.region}</span>
                      </button>
                    ))}
                  </div>
                )}
                {editable && (
                  <button type="button" onClick={() => setFallback((v) => !v)} data-testid="facility-fallback-toggle"
                    className="text-xs text-primary hover:underline mt-1">
                    Can't find your facility? Browse by region →
                  </button>
                )}
              </div>

              {(fallback || portal) && editable && (
                <div className="grid sm:grid-cols-3 gap-4 rounded-md bg-muted/50 p-4">
                  <div className="space-y-1.5">
                    <Label>Region</Label>
                    <Select value={form.region_name || ""} onValueChange={pickRegion}>
                      <SelectTrigger data-testid="fallback-region"><SelectValue placeholder="Region" /></SelectTrigger>
                      <SelectContent>{refRegions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>District</Label>
                    <Select value={form.district || ""} onValueChange={pickDistrict} disabled={!refDistricts.length}>
                      <SelectTrigger data-testid="fallback-district"><SelectValue placeholder="District" /></SelectTrigger>
                      <SelectContent>{refDistricts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Facility</Label>
                    <Select value={form.facility_name || ""} onValueChange={pickRefFacility} disabled={!refFacilities.length}>
                      <SelectTrigger data-testid="fallback-facility"><SelectValue placeholder="Facility" /></SelectTrigger>
                      <SelectContent>{refFacilities.map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}</div>),

              facility_type: (<div className="space-y-1.5">
                <Label htmlFor="profile-facility-type">{L("facility_type", "Type of Facility")} <span className="text-destructive">*</span></Label>
                <Select value={form.facility_type || ""} onValueChange={(v) => set("facility_type", v)} disabled={!editable}>
                  <SelectTrigger id="profile-facility-type" data-testid="profile-facility-type" aria-required="true"><SelectValue placeholder="Select facility type" /></SelectTrigger>
                  <SelectContent>{FACILITY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`facility-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>),
              town: <div className="space-y-1.5"><Label>{L("town", "Town")}</Label><Input data-testid="profile-town" value={form.town || ""} disabled /></div>,
              district: <div className="space-y-1.5"><Label>{L("district", "District")}</Label><Input data-testid="profile-district" value={form.district || ""} disabled /></div>,
              region: <div className="space-y-1.5"><Label>{L("region", "Region")}</Label><Input data-testid="profile-region" value={form.region_name || ""} disabled /></div>,
              ghana_card_number: (<div className="space-y-1.5">
                <Label>{L("ghana_card_number", "Ghana Card Number")} <span className="text-destructive">*</span></Label>
                <Input id="profile-ghana-card" data-testid="profile-ghana-card" value={form.ghana_card_number || ""} disabled={!editable}
                  inputMode="numeric" aria-label="Ghana Card Number" aria-required="true" aria-invalid={!!errors.ghana_card_number}
                  placeholder="GHA-XXXXXXXXX-X" onChange={(e) => {
                    const input = e.target;
                    const digitPosition = input.value.slice(0, input.selectionStart).replace(/\D/g, "").length;
                    const formatted = formatGhanaCard(input.value);
                    set("ghana_card_number", formatted);
                    requestAnimationFrame(() => {
                      const position = formatted ? 4 + digitPosition + (digitPosition > 9 ? 1 : 0) : 0;
                      input.setSelectionRange(position, position);
                    });
                  }} />
                <p className="text-xs text-muted-foreground" data-testid="ghana-card-format-hint">Type the 10 digits; GHA- and the dash are added automatically.</p>
                {gcCheck?.checking && <p className="text-xs text-muted-foreground" data-testid="gc-checking" role="status">Checking availability…</p>}
                {gcCheck?.unavailable && <p className="text-xs text-muted-foreground" data-testid="gc-unavailable">Live check unavailable. Your number will be checked when you save.</p>}
                {gcCheck && !gcCheck.checking && (
                  gcCheck.valid_format === false ? (
                    <p className="text-xs text-destructive" data-testid="gc-feedback">Format must be GHA-XXXXXXXXX-X</p>
                  ) : gcCheck.available === false ? (
                    <p className="text-xs text-destructive" data-testid="gc-feedback">This Ghana Card is already registered</p>
                  ) : gcCheck.available === true ? (
                    <p className="text-xs text-emerald-600" data-testid="gc-feedback">Valid & available ✓</p>
                  ) : null
                )}
              </div>),

              ec_name: !intern && (<div className="space-y-1.5">
                <Label>{L("ec_name", "Full Name")} <span className="text-destructive">*</span></Label>
                <Input data-testid="profile-ec-name" value={form.emergency_contact_name || ""} disabled={!editable}
                  onChange={(e) => set("emergency_contact_name", e.target.value)} />
              </div>),
              ec_phone: !intern && (<div className="space-y-1.5">
                <Label>{L("ec_phone", "Phone (single number)")} <span className="text-destructive">*</span></Label>
                <Input data-testid="profile-ec-phone" type="tel" aria-label="Emergency contact phone" aria-required="true" value={form.emergency_contact_phone || ""} disabled={!editable}
                  placeholder="e.g. 0241234567" onChange={(e) => set("emergency_contact_phone", e.target.value)} />
              </div>),
              ec_email: !intern && (<div className="space-y-1.5">
                <Label>{L("ec_email", "Email (optional)")}</Label>
                <Input data-testid="profile-ec-email" type="email" value={form.emergency_contact_email || ""} disabled={!editable}
                  onChange={(e) => set("emergency_contact_email", e.target.value)} />
              </div>),
              ec_address: !intern && (<div className="space-y-1.5">
                <Label>{L("ec_address", "Address (GPS digital or street)")} <span className="text-destructive">*</span></Label>
                <Input data-testid="profile-ec-address" value={form.emergency_contact_address || ""} disabled={!editable}
                  placeholder="e.g. GA-123-4567 or 12 High St, Accra" onChange={(e) => set("emergency_contact_address", e.target.value)} />
              </div>),

              marital_status: !intern && (<div className="grid sm:grid-cols-2 gap-4"><div className="space-y-1.5">
                <Label>{L("marital_status", "Marital Status")} <span className="text-destructive">*</span></Label>
                <Select value={form.marital_status || ""} onValueChange={(v) => { set("marital_status", v); if (v !== "Married") set("spouse_name", ""); }} disabled={!editable}>
                  <SelectTrigger data-testid="profile-marital-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>{MARITAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.marital_status === "Married" && (
                <div className="space-y-1.5">
                  <Label>Spouse's Name <span className="text-destructive">*</span></Label>
                  <Input data-testid="profile-spouse-name" value={form.spouse_name || ""} disabled={!editable}
                    onChange={(e) => set("spouse_name", e.target.value)} />
                </div>
              )}</div>),
              children: !intern && (<div className="grid sm:grid-cols-2 gap-4"><ChildrenEditor childrenList={form.children} noChildren={form.no_children} editable={editable}
                onChange={(next) => setForm((p) => ({ ...p, ...next }))} /></div>),
              mother_name: !intern && (<div className="space-y-1.5">
                <Label>{L("mother_name", "Mother's Name")} <span className="text-destructive">*</span></Label>
                <Input data-testid="profile-mother-name" value={form.mother_name || ""} disabled={!editable}
                  onChange={(e) => set("mother_name", e.target.value)} />
              </div>),
              mother_status: !intern && (<div className="space-y-1.5">
                <Label>{L("mother_status", "Mother's Status")} <span className="text-destructive">*</span></Label>
                <Select value={form.mother_status || ""} onValueChange={(v) => set("mother_status", v)} disabled={!editable}>
                  <SelectTrigger data-testid="profile-mother-status"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PARENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>),
              father_name: !intern && (<div className="space-y-1.5">
                <Label>{L("father_name", "Father's Name")} <span className="text-destructive">*</span></Label>
                <Input data-testid="profile-father-name" value={form.father_name || ""} disabled={!editable}
                  onChange={(e) => set("father_name", e.target.value)} />
              </div>),
              father_status: !intern && (<div className="space-y-1.5">
                <Label>{L("father_status", "Father's Status")} <span className="text-destructive">*</span></Label>
                <Select value={form.father_status || ""} onValueChange={(v) => set("father_status", v)} disabled={!editable}>
                  <SelectTrigger data-testid="profile-father-status"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PARENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>),

              previous_employment: !intern && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{L("previous_employment", "Have you worked at another facility?")} <span className="text-destructive">*</span></Label>
                <div role="group" aria-label="Have you worked at another facility?" data-testid="profile-prev-worked" className="flex gap-2">
                  {["yes", "no"].map((value) => <Button key={value} type="button" disabled={!editable}
                    variant={form.worked_at_previous_facility === value ? "default" : "outline"}
                    aria-pressed={form.worked_at_previous_facility === value} data-testid={`profile-prev-worked-${value}`}
                    onClick={() => setForm((previous) => ({ ...previous, worked_at_previous_facility: value,
                      ...(value === "no" ? { previous_facility_name: "", previous_facility_location: "", previous_facility_type: "" } : {}) }))}>
                    {value === "yes" ? "Yes" : "No"}
                  </Button>)}
                </div>
              </div>
              {form.worked_at_previous_facility === "yes" && (<>
                <PreviousFacilityPicker value={form.previous_facility_name} mode={prevMode} editable={editable}
                  onType={(text) => set("previous_facility_name", text)}
                  onPick={(f) => setForm((p) => ({ ...p, previous_facility_name: f.name, previous_facility_location: f.location || p.previous_facility_location, previous_facility_type: f.type || p.previous_facility_type }))}
                  onOther={(on) => { setPrevMode(on ? OTHER_FACILITY : ""); if (on) setForm((p) => ({ ...p, previous_facility_name: "", previous_facility_location: "", previous_facility_type: "" })); }} />
                <div className="space-y-1.5">
                  <Label>Location <span className="text-destructive">*</span></Label>
                  <Input data-testid="profile-prev-location" value={form.previous_facility_location || ""} disabled={!editable}
                    onChange={(e) => set("previous_facility_location", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Facility Type <span className="text-destructive">*</span></Label>
                  <Select value={form.previous_facility_type || ""} onValueChange={(v) => set("previous_facility_type", v)} disabled={!editable}>
                    <SelectTrigger data-testid="profile-prev-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{FACILITY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`previous-facility-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>)}
            </div>),

              dues: !intern && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{L("dues", "Are you paying dues?")} <span className="text-destructive">*</span></Label>
                <Select value={form.pays_dues || ""} onValueChange={(v) => { set("pays_dues", v); if (v === "no") set("dues_mode", ""); }} disabled={!editable}>
                  <SelectTrigger data-testid="profile-pays-dues"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.pays_dues === "yes" && (
                <div className="space-y-1.5">
                  <Label>Mode of payment <span className="text-destructive">*</span></Label>
                  <Select value={form.dues_mode || ""} onValueChange={(v) => set("dues_mode", v)} disabled={!editable}>
                    <SelectTrigger data-testid="profile-dues-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Self payment</SelectItem>
                      <SelectItem value="cagd">CAGD payroll deduction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.pays_dues && (
                <span data-testid="dues-status-badge"
                  className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${form.pays_dues === "yes" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                  {form.pays_dues === "yes" ? "Dues paying" : "Non dues paying"}
                </span>
              )}
            </div>),

              tshirt_size: !intern && form.pays_dues !== "no" && (
              <div className="space-y-1.5">
                <Label>{L("tshirt_size", "T-Shirt Size")} <span className="text-destructive">*</span></Label>
                <Select value={form.tshirt_size || ""} onValueChange={(v) => set("tshirt_size", v)} disabled={!editable}>
                  <SelectTrigger data-testid="profile-tshirt-size"><SelectValue placeholder="Select size" /></SelectTrigger>
                  <SelectContent>{TSHIRT_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>),

              education: (<div>
            {education.length === 0 && <p className="text-sm text-muted-foreground">No institutions added yet.</p>}
            <div className="space-y-4">
              {education.map((e, i) => (
                <div key={i} className="rounded-md border border-border p-4" data-testid={`education-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Institution {i + 1}</p>
                    {editable && <Button type="button" size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => rmEdu(i)} data-testid={`remove-education-${i}`}><Trash size={16} /></Button>}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Name of institution</Label><Input data-testid={`edu-institution-${i}`} value={e.institution} disabled={!editable} onChange={(ev) => setEdu(i, "institution", ev.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Location</Label><Input value={e.location} disabled={!editable} onChange={(ev) => setEdu(i, "location", ev.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Year of entry</Label><Input value={e.entry_year} disabled={!editable} onChange={(ev) => setEdu(i, "entry_year", ev.target.value)} placeholder="e.g. 2005" /></div>
                    <div className="space-y-1.5"><Label>Year of graduation</Label><Input value={e.graduation_year} disabled={!editable} onChange={(ev) => setEdu(i, "graduation_year", ev.target.value)} placeholder="e.g. 2009" /></div>
                    <div className="space-y-1.5 sm:col-span-2"><Label>Qualification earned</Label><Input value={e.qualification} disabled={!editable} onChange={(ev) => setEdu(i, "qualification", ev.target.value)} /></div>
                  </div>
                </div>
              ))}
            </div>
            {editable && education.length < 5 && (
              <Button type="button" variant="outline" className="gap-2 mt-4" onClick={addEdu} data-testid="add-education-button">
                <Plus size={16} weight="bold" /> Add another institution
              </Button>
            )}
            {education.length >= 5 && <p className="text-xs text-muted-foreground mt-3">Maximum of 5 institutions reached.</p>}
          </div>),
          })}

          {applicationFlow && (
            <div className="flex flex-wrap gap-3 max-sm:sticky max-sm:bottom-0 max-sm:-mx-4 max-sm:px-4 max-sm:py-3 max-sm:bg-background/90 max-sm:backdrop-blur max-sm:border-t max-sm:border-border max-sm:z-10">
              <Button variant="outline" onClick={save} disabled={saving || submitting || photoUploading} data-testid="save-profile-button" className="gap-2 max-sm:flex-1">
                <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button onClick={submit} disabled={submitting || saving || photoUploading} data-testid="submit-application-button" className="gap-2 max-sm:flex-1">
                <PaperPlaneTilt size={18} weight="bold" /> {submitting ? "Submitting…" : "Submit application"}
              </Button>
            </div>
          )}

          {editing && editMode && (
            <div className="flex flex-wrap gap-3 max-sm:sticky max-sm:bottom-0 max-sm:-mx-4 max-sm:px-4 max-sm:py-3 max-sm:bg-background/90 max-sm:backdrop-blur max-sm:border-t max-sm:border-border max-sm:z-10">
              <Button onClick={submitEdits} disabled={submitting || saving || photoUploading} data-testid="submit-edits-button" className="gap-2 max-sm:flex-1">
                <PaperPlaneTilt size={18} weight="bold" /> {submitting ? "Saving…" : "Save changes for National approval"}
              </Button>
              <Button variant="outline" onClick={cancelEdit} disabled={submitting} className="max-sm:flex-1" data-testid="cancel-edit-profile-bottom">Cancel</Button>
            </div>
          )}
        </div>
      </div>

    </Layout>
  );
}
